import { ConstantContent } from '@sensenet/client-core';
import { PathHelper } from '@sensenet/client-utils';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import Semaphore from 'semaphore-async-await';
import { useRepository, useRepositoryEvents } from "../hooks/index.js";
/**
 * Returns a given content as current content
 */
export const CurrentContentContext = /*#__PURE__*/createContext(ConstantContent.PORTAL_ROOT);
CurrentContentContext.displayName = 'CurrentContentContext';
/**
 * Provider component for the CurrentContentContext component.
 * Loads a content from the Repository with the given Id or Path.
 * Has to be wrapped with a **RepositoryContext**
 */
export const CurrentContentProvider = props => {
  const [loadLock] = useState(new Semaphore(1));
  const [content, setContent] = useState(ConstantContent.EMPTY_CONTENT);
  const [errorState, setErrorState] = useState();
  const [reloadToken, setReloadToken] = useState(1);
  const reload = () => setReloadToken(Math.random());
  const repo = useRepository();
  const events = useRepositoryEvents();
  const onDeleteContent = useCallback(async deleted => {
    if (deleted.some(deletedContent => PathHelper.isInSubTree(content.Path, deletedContent.Path))) {
      const parentContent = await repo.load({
        idOrPath: PathHelper.getParentPath(deleted[0].Path)
      });
      setContent(parentContent.d);
    }
  }, [repo, content]);
  useEffect(() => {
    const subscriptions = [events.onContentModified.subscribe(c => {
      if (c.content.Id === content.Id) {
        reload();
      }
    }), events.onContentDeleted.subscribe(async c => {
      onDeleteContent(c.contentData);
    })];
    return () => subscriptions.forEach(s => s.dispose());
  }, [content.Id, onDeleteContent, events.onContentDeleted, events.onContentModified, repo]);
  useEffect(() => {
    const ac = new AbortController();
    if (props.idOrPath) {
      ;
      (async () => {
        try {
          const response = await repo.load({
            idOrPath: props.idOrPath,
            requestInit: {
              signal: ac.signal
            },
            oDataOptions: props.oDataOptions
          });
          setContent(response.d);
          props.onContentLoaded && props.onContentLoaded(response.d);
        } catch (error) {
          if (!ac.signal.aborted) {
            var _error$response;
            setErrorState({
              currentPath: props.idOrPath,
              status: (_error$response = error.response) === null || _error$response === void 0 ? void 0 : _error$response.status,
              error
            });
          }
        } finally {
          loadLock.release();
        }
      })();
    }
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo, props.idOrPath, reloadToken, loadLock]);
  if (errorState !== null && errorState !== void 0 && errorState.error) {
    if ((errorState === null || errorState === void 0 ? void 0 : errorState.status) === 404) {
      throw errorState;
    }
    throw errorState.error;
  }
  return /*#__PURE__*/React.createElement(CurrentContentContext.Provider, {
    value: content
  }, props.children);
};
//# sourceMappingURL=current-content.js.map